import {
  CONSOLE_ROUTES,
  type ConsoleEvalCase,
  type ConsoleEvalCaseCreateRequest,
  ConsoleEvalCaseSchema,
  ConsoleEvalCasesSchema,
  type ConsoleEvalCaseUpdateRequest,
  type ConsoleEvalRun,
  type ConsoleEvalRunDetail,
  ConsoleEvalRunDetailSchema,
  ConsoleEvalRunSchema,
  ConsoleEvalRunsSchema,
  type ConsolePreviewSession,
  ConsolePreviewSessionSchema,
  type ConsoleTestAccount,
  type ConsoleTestAccountRegisterRequest,
  ConsoleTestAccountSchema,
  ConsoleTestAccountsSchema,
  type TenantId,
} from '@plato/shared';
import { callConsoleApi, sendToConsoleApi } from './http-client.ts';

export interface EvalsConsoleData {
  listEvalCases(tenant: TenantId): Promise<readonly ConsoleEvalCase[]>;
  createEvalCase(tenant: TenantId, fields: ConsoleEvalCaseCreateRequest): Promise<ConsoleEvalCase>;
  updateEvalCase(
    tenant: TenantId,
    caseId: string,
    fields: ConsoleEvalCaseUpdateRequest,
  ): Promise<ConsoleEvalCase>;
  archiveEvalCase(tenant: TenantId, caseId: string): Promise<void>;

  listEvalRuns(tenant: TenantId): Promise<readonly ConsoleEvalRun[]>;
  getEvalRun(tenant: TenantId, runId: string): Promise<ConsoleEvalRunDetail>;
  startEvalRun(tenant: TenantId, caseIds?: readonly string[]): Promise<ConsoleEvalRun>;

  listTestAccounts(tenant: TenantId): Promise<readonly ConsoleTestAccount[]>;
  registerTestAccount(
    tenant: TenantId,
    account: ConsoleTestAccountRegisterRequest,
  ): Promise<ConsoleTestAccount>;
  removeTestAccount(tenant: TenantId, name: string): Promise<void>;

  openPreviewSession(
    tenant: TenantId,
    testAccountId: string | null,
  ): Promise<ConsolePreviewSession>;
}

export const httpEvalsData: EvalsConsoleData = {
  listEvalCases: async (tenant) =>
    (await callConsoleApi(CONSOLE_ROUTES.evalCases(tenant), ConsoleEvalCasesSchema)).cases,
  createEvalCase: (tenant, fields) =>
    callConsoleApi(CONSOLE_ROUTES.evalCases(tenant), ConsoleEvalCaseSchema, {
      method: 'POST',
      body: fields,
    }),
  updateEvalCase: (tenant, caseId, fields) =>
    callConsoleApi(CONSOLE_ROUTES.evalCase(tenant, caseId), ConsoleEvalCaseSchema, {
      method: 'PATCH',
      body: fields,
    }),
  archiveEvalCase: (tenant, caseId) =>
    sendToConsoleApi(CONSOLE_ROUTES.evalCaseArchive(tenant, caseId), { method: 'POST' }),

  listEvalRuns: async (tenant) =>
    (await callConsoleApi(CONSOLE_ROUTES.evalRuns(tenant), ConsoleEvalRunsSchema)).runs,
  getEvalRun: (tenant, runId) =>
    callConsoleApi(CONSOLE_ROUTES.evalRun(tenant, runId), ConsoleEvalRunDetailSchema),
  startEvalRun: (tenant, caseIds) =>
    callConsoleApi(CONSOLE_ROUTES.evalRuns(tenant), ConsoleEvalRunSchema, {
      method: 'POST',
      body: caseIds === undefined ? {} : { case_ids: [...caseIds] },
    }),

  listTestAccounts: async (tenant) =>
    (await callConsoleApi(CONSOLE_ROUTES.testAccounts(tenant), ConsoleTestAccountsSchema)).accounts,
  registerTestAccount: (tenant, account) =>
    callConsoleApi(CONSOLE_ROUTES.testAccounts(tenant), ConsoleTestAccountSchema, {
      method: 'POST',
      body: account,
    }),
  removeTestAccount: (tenant, name) =>
    sendToConsoleApi(CONSOLE_ROUTES.testAccount(tenant, name), { method: 'DELETE' }),

  openPreviewSession: (tenant, testAccountId) =>
    callConsoleApi(CONSOLE_ROUTES.previewSessions(tenant), ConsolePreviewSessionSchema, {
      method: 'POST',
      body: { test_account_id: testAccountId },
    }),
};
